import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Alert, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { formatHeight, buildHeightOptions } from '../../utils/heightUtils';
import { findApplicableGearWeight, estimateBodyWeight, computeDisplayWeight, formatDisplayWeight } from '../../utils/gearWeightUtils';
import { addMeasurement, updateMeasurement, removeMeasurement } from '../../services/athleteService';

const HEIGHT_OPTIONS = buildHeightOptions();
const today = () => new Date().toISOString().slice(0, 10);

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
}

const GEAR_LABELS = { no_gear: 'No Gear', with_gear: 'With Gear', unknown: 'Gear Unknown' };
function gearLabel(status) {
  return GEAR_LABELS[status] || 'Gear Unknown';
}

/**
 * allowUnknownGear: shows an "Unknown" option in the gear-status choice.
 * Only relevant when editing a pre-existing legacy row — normal new entry
 * only ever chooses No Gear or With Gear (enforced server-side too).
 */
function MeasurementForm({ initial, onSave, onCancel, saving, error, allowUnknownGear }) {
  const [date, setDate] = useState(initial?.measurement_date || today());
  const [height, setHeight] = useState(initial?.height_in ?? '');
  const [weight, setWeight] = useState(initial?.weight_lb ?? '');
  const [gearStatus, setGearStatus] = useState(initial?.weight_gear_status || 'no_gear');

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          type="date" label="Date" size="small" value={date} onChange={e => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} sx={{ minWidth: 150 }}
        />
        <TextField
          select label="Height" size="small" value={height} onChange={e => setHeight(e.target.value)}
          helperText=" " sx={{ minWidth: 130 }}
        >
          <MenuItem value="">—</MenuItem>
          {HEIGHT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          type="number" label="Weight (lb)" size="small" value={weight} onChange={e => setWeight(e.target.value)}
          inputProps={{ min: 0, step: 0.1 }} sx={{ minWidth: 130 }}
        />
      </Box>

      <RadioGroup
        row value={gearStatus} onChange={e => setGearStatus(e.target.value)}
        sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
      >
        <FormControlLabel value="no_gear" control={<Radio size="small" />} label="Without Gear" />
        <FormControlLabel value="with_gear" control={<Radio size="small" />} label="With Gear" />
        {allowUnknownGear && <FormControlLabel value="unknown" control={<Radio size="small" />} label="Unknown" />}
      </RadioGroup>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
        <Button
          variant="contained" color="primary" disabled={saving}
          onClick={() => onSave({
            measurementDate: date,
            heightIn: height === '' ? null : Number(height),
            weightLb: weight === '' ? null : Number(weight),
            weightGearStatus: gearStatus,
          })}
          sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save'}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8', height: 40 }}>
          Cancel
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

/**
 * Growth / Physical Development — current height/weight (server-maintained
 * cache on the athlete record) plus the active dated history. Adding a
 * measurement merges into that day's active record for the SAME gear
 * status if one already exists; a no_gear and a with_gear reading on the
 * same date are separate, independently visible rows, never merged into
 * one number. Correcting or removing an existing row is available only to
 * whoever originally entered it (or a platform admin) and always goes
 * through the corresponding RPC — never a direct table write. Removing is
 * a soft delete: excluded from history and the current-value cache, never
 * physically deleted, no restore action in this pass.
 *
 * Current Weight (shown above) is a DISPLAY value computed by
 * computeDisplayWeight (gearWeightUtils) — not read directly off
 * athlete.weight_lb. The server-side cache (athlete.weight_lb) is still
 * strictly actual no_gear weight, falling back to legacy 'unknown', and
 * never an estimate; this display layer sits on top of the same
 * measurements + gearWeights data and additionally falls back to a
 * with_gear reading (estimated via gear load if one applies, or shown
 * as the raw scale reading with a "(with gear)" qualifier otherwise)
 * only once both no_gear and unknown have come up empty — so the
 * profile shows a real number instead of a dash whenever any weight data
 * exists at all, while an actual no_gear reading always still wins over
 * an estimate. See computeDisplayWeight's own doc comment for the full
 * priority order.
 *
 * weight_gear_status describes the WEIGHT value only, not the whole row —
 * a with_gear row may still carry a height (the height was measured
 * separately from the gear-affected scale reading). Current Height is
 * the latest active height regardless of gear status; the server allows
 * at most one active height per athlete/date across all gear statuses,
 * surfaced here as a clean error if an add/edit would create a second
 * one for the same day.
 *
 * A with_gear row's weight also gets a derived, display-only "Estimated
 * Body Weight" underneath it in the history table when gearWeights has
 * an applicable entry (see gearWeightUtils) — never stored, never
 * affecting the raw 72-lb history value.
 *
 * Deliberately not a chart in this pass — the goal here is preserving and
 * displaying history correctly, not visualization.
 */
export default function MeasurementHistory({ athlete, measurements, gearWeights, currentUserId, isAdmin, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [removingId, setRemovingId] = useState(null); // id pending confirmation
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const handleAdd = async (values) => {
    setSaving(true);
    setFormError(null);
    try {
      await addMeasurement({ athleteId: athlete.id, ...values });
      setAdding(false);
      await onChanged();
    } catch (e) {
      setFormError(e.message || 'Could not save measurement.');
    } finally {
      setSaving(false);
    }
  };

  const handleCorrect = async (measurementId, values) => {
    setSaving(true);
    setFormError(null);
    try {
      await updateMeasurement(measurementId, values);
      setEditingId(null);
      await onChanged();
    } catch (e) {
      setFormError(e.message || 'Could not save correction.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    setSaving(true);
    setRemoveError(null);
    try {
      await removeMeasurement(removingId);
      setRemovingId(null);
      await onChanged();
    } catch (e) {
      setRemoveError(e.message || 'Could not remove that measurement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mt: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Growth / Physical Development</Typography>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
          Height and weight over time. Not a medical growth chart — just this athlete's own measurement history.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em' }}>CURRENT HEIGHT</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>{athlete.height_in ? formatHeight(athlete.height_in) : '—'}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em' }}>CURRENT WEIGHT</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>{formatDisplayWeight(computeDisplayWeight(measurements, gearWeights))}</Typography>
          </Box>
        </Box>

        {!adding ? (
          <Button size="small" variant="outlined" color="primary" onClick={() => { setAdding(true); setFormError(null); }} sx={{ textTransform: 'none', fontWeight: 700, mb: 2 }}>
            + Add Measurement
          </Button>
        ) : (
          <Box sx={{ mb: 2.5, p: 2, border: '1px solid #1e2a3a', borderRadius: 2 }}>
            <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 1.5 }}>
              If there's already a record for this date and gear status, your values are added to it — it won't create a duplicate entry.
            </Typography>
            <MeasurementForm onSave={handleAdd} onCancel={() => { setAdding(false); setFormError(null); }} saving={saving} error={formError} allowUnknownGear={false} />
          </Box>
        )}

        {measurements.length === 0 ? (
          <Typography sx={{ color: '#6a8aa8' }}>No measurements recorded yet.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Height</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {measurements.map(m => {
                  const canEdit = m.entered_by === currentUserId || isAdmin;
                  if (editingId === m.id) {
                    return (
                      <TableRow key={m.id}>
                        <TableCell colSpan={4}>
                          <MeasurementForm
                            initial={m}
                            onSave={(values) => handleCorrect(m.id, values)}
                            onCancel={() => { setEditingId(null); setFormError(null); }}
                            saving={saving}
                            error={formError}
                            allowUnknownGear
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  const applicableGear = m.weight_gear_status === 'with_gear'
                    ? findApplicableGearWeight(gearWeights, m.measurement_date)
                    : null;
                  const estimatedBodyWeight = applicableGear
                    ? estimateBodyWeight(m.weight_lb, applicableGear.gear_weight_lb)
                    : null;
                  return (
                    <TableRow key={m.id}>
                      <TableCell>{formatDate(m.measurement_date)}</TableCell>
                      <TableCell>{m.height_in ? formatHeight(m.height_in) : <Chip size="small" label="—" sx={{ bgcolor: 'transparent', color: '#4a6080' }} />}</TableCell>
                      <TableCell>
                        {m.weight_lb ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <span>{m.weight_lb} lb</span>
                              <Chip
                                size="small" label={gearLabel(m.weight_gear_status)}
                                sx={{
                                  fontSize: '0.65rem', height: 18,
                                  bgcolor: m.weight_gear_status === 'with_gear' ? '#2a1405' : m.weight_gear_status === 'unknown' ? '#131318' : '#0a2a14',
                                  color: m.weight_gear_status === 'with_gear' ? '#f5a623' : m.weight_gear_status === 'unknown' ? '#6a8aa8' : '#2ea84b',
                                }}
                              />
                            </Box>
                            {estimatedBodyWeight != null && (
                              <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', mt: 0.25 }}>
                                Estimated Body Weight: {estimatedBodyWeight} lb
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Chip size="small" label="—" sx={{ bgcolor: 'transparent', color: '#4a6080' }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canEdit && (
                          <>
                            <IconButton size="small" onClick={() => { setEditingId(m.id); setAdding(false); setFormError(null); }} aria-label="Edit measurement" sx={{ color: '#6a8aa8' }}>
                              ✎
                            </IconButton>
                            <IconButton size="small" onClick={() => { setRemovingId(m.id); setRemoveError(null); }} aria-label="Remove measurement" sx={{ color: '#6a8aa8' }}>
                              ✕
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      <Dialog open={removingId !== null} onClose={() => !saving && setRemovingId(null)}>
        <DialogTitle>Remove this measurement from {athlete.first_name}'s history?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>
            It won't be shown in the normal history, and Current Height/Weight will update if this was the most recent value.
          </Typography>
          {removeError && <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingId(null)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirmed} disabled={saving} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
